import { Avatar } from "antd";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import HeroIconSrc from "shared/assets/images/hero-colors.png";
import { TypingEffect } from "../Quincy/TypingEffect";
import { Trans, useTranslation } from "react-i18next";
import {
  canAskAdvance,
  getLoggedUserCashAdvanceAvailable,
} from "explorer/reducers/adiantamentos";
import { formatNoSignObject, signByCurrency } from "shared/helpers/BRLFormat";
import { HeroSteps } from "./Hero";
import {
  createNewCashAdvance,
  fetchAdvanceDataLoggedUser,
} from "shared/service/StrmService";
import { getLoggedUser } from "explorer/reducers/users";
import { loginReduced } from "explorer/reducers/auth";
import {
  autenticado,
  handlePendingHero,
  hasHeroPending,
} from "shared/helpers/auth-functions";
import {
  getLoggedUserArtist,
  getUserDistroToken,
} from "explorer/reducers/artistas";
import { isDesktop } from "react-device-detect";
import HeroLines from "shared/assets/images/hero-lines.png";
import StrmHeroLogo from "shared/assets/images/strm-hero-logo.png";
import {
  dataLayerViewedEvent,
  tagManagerEvent,
} from "event-tracker/gtm-events";
import { useHistory } from "react-router-dom";
import { getStrmClassVideos } from "explorer/reducers/strm-class";
import { getMonitoredData } from "explorer/reducers/monitored-data";
import { getUserSubscription } from "explorer/reducers/subscription";

interface HeroGreetingsProps {
  setCurrentStep: any;
}

enum ActionOptions {
  WithAdvance = "WithAdvance",
  WithoutAdvance = "WithoutAdvance",
}

const HeroGreetings: React.FC<HeroGreetingsProps> = (props) => {
  const { setCurrentStep } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const containerRef = useRef<HTMLDivElement>(null);
  const [actionOptions, setActionOptions] = useState<ActionOptions>();
  const [loading, setLoading] = useState<boolean>(false);
  const { userArtist } = useSelector((state: any) => state.artists);
  const { loggedUser } = useSelector((state: any) => state.users);
  const [hasAdvance, setHasAdvance] = useState<boolean>(false);
  const { cashAdvanceAvailable, loading: loadingAdvance } = useSelector(
    (state: any) => state.advances
  );

  const [fullText, setFullText] = useState<string>("");

  const onTextChange = () => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  };

  const onTextComplete = () => {
    setActionOptions(
      canAskAdvance(cashAdvanceAvailable)
        ? ActionOptions.WithAdvance
        : ActionOptions.WithoutAdvance
    );
  };

  const onClickAcceptWithAdvance = async () => {
    if (!hasAdvance) await createNewCashAdvance(loggedUser.id);
    history.push("/adiantamentos");
  };

  useEffect(() => {
    if (userArtist?.stage && !loadingAdvance) {
      const advanceAmount = `${signByCurrency(
        userArtist?.cashAdvanceEstimated?.currency
      )} ${formatNoSignObject({
        currency: userArtist?.cashAdvanceEstimated?.currency,
        amount:
          userArtist?.cashAdvanceEstimated?.amount /
          userArtist?.cashAdvanceEstimated?.trancheFactor,
      })}`;
      const advanceAmountTranche = `${signByCurrency(
        userArtist?.cashAdvanceEstimated?.currency
      )} ${formatNoSignObject(userArtist?.cashAdvanceEstimated)}`;

      const phonograms = userArtist?.singles + userArtist?.albuns;
      let startText = t("pages.StrmHero.analysisStart", { phonograms });
      let complementText = "";
      if (canAskAdvance(cashAdvanceAvailable)) {
        complementText = t("pages.StrmHero.analysisStartAdvance", {
          advanceAmount,
          advanceAmountTranche,
        });
      } else {
        complementText = t("pages.StrmHero.analysisStartNoAdvance", {
          phonograms,
          currency: signByCurrency(userArtist?.cashAdvanceEstimated?.currency),
        });
      }
      if (startText && complementText) {
        console.log("Start text");
        setFullText(startText + complementText);
      }
    }
  }, [userArtist, cashAdvanceAvailable, loadingAdvance, t]);

  useEffect(() => {
    const handleInfo = async () => {
      if (autenticado()) {
        if (hasHeroPending()) {
          setLoading(true);
          await handlePendingHero(() => {
            dispatch(getLoggedUser());
            dispatch(getLoggedUserArtist());
            dispatch(getMonitoredData());
            dispatch(loginReduced());
            dispatch(getLoggedUserCashAdvanceAvailable(true));
            dispatch(getStrmClassVideos());
            dispatch(getUserDistroToken());
            dispatch(getUserSubscription());
            setLoading(false);
          });
        } else {
          dispatch(getLoggedUserCashAdvanceAvailable(false));
        }
      }
    };
    handleInfo();
  }, [dispatch]);

  useEffect(() => {
    dataLayerViewedEvent({
      ...tagManagerEvent.Hero.S4A_HERO_GREETINGS,
    });
  }, []);

  useEffect(() => {
    const getAdvance = async () => {
      const response: any = await fetchAdvanceDataLoggedUser();
      setHasAdvance(!!response);
    };

    getAdvance();
  }, []);

  if (loading)
    return (
      <div className="verification full-height">
        <div
          className="background-center"
          style={{
            backgroundImage: `url(${HeroLines})`,
            backgroundPositionY: isDesktop ? "30%" : "calc(50% + 60px)",
          }}
        >
          <div style={{ padding: "0 40px", textAlign: "center" }}>
            <img src={StrmHeroLogo} alt="logo" style={{ width: 94 }} />
            <p className="body-regular" style={{ marginTop: "10vh" }}>
              <Trans i18nKey={t("pages.StrmHero.analysingData")} />
            </p>
          </div>
        </div>
      </div>
    );

  return (
    <div className="greetings full-height">
      <div
        className="background-colors"
        style={{
          backgroundImage: `url(${HeroIconSrc})`,
        }}
      >
        <div style={{ padding: "0 40px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            className="height-30"
          >
            <Avatar size={120} src={userArtist?.profileImage} />
          </div>

          <div
            ref={containerRef}
            className="scrollable height-70"
            style={{ paddingBottom: 60 }}
          >
            <h2>Greetings {userArtist?.name}</h2>
            <div className="body-regular" style={{ fontSize: 16 }}>
              <TypingEffect
                fullText={fullText}
                onTextChange={onTextChange}
                onComplete={onTextComplete}
              />
            </div>
            {actionOptions && (
              <div className="options-advance">
                {actionOptions === ActionOptions.WithAdvance && (
                  <>
                    <button
                      className="button-option"
                      onClick={onClickAcceptWithAdvance}
                    >
                      {t("pages.StrmHero.acceptWithAdvance")}
                    </button>
                    <button
                      className="button-option"
                      onClick={() => setCurrentStep(HeroSteps.Portal)}
                    >
                      {t("pages.StrmHero.acceptWithRecommendations")}
                    </button>
                    <button
                      className="button-option"
                      onClick={() => setCurrentStep(HeroSteps.Goodbye)}
                    >
                      {t("pages.StrmHero.notAceeptDinossaurs")}
                    </button>
                  </>
                )}
                {actionOptions === ActionOptions.WithoutAdvance && (
                  <>
                    <button
                      className="button-option"
                      onClick={() => setCurrentStep(HeroSteps.Portal)}
                    >
                      {t("pages.StrmHero.yesSure")}
                    </button>
                    <button
                      className="button-option"
                      onClick={() => setCurrentStep(HeroSteps.Goodbye)}
                    >
                      {t("pages.StrmHero.notNow")}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroGreetings;
